import React, { useRef, useEffect, useState } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useAuth } from '@/context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JobCardPrint = ({ job, process, workOrder, autoPrint = false, onPrintComplete }) => {
  const hasTriggeredPrint = useRef(false);
  const { getAuthHeader } = useAuth();
  // Fix Bug 11b — when job is OJC, fetch the register doc so print shows
  // jobber_name / expected_rate_per_unit / total_labour_charges.
  const [ojcDoc, setOjcDoc] = useState(null);
  const [ojcFetchResolved, setOjcFetchResolved] = useState(false);
  const isOjc = Boolean(job?.is_outsourced || job?.ojc_id);

  useEffect(() => {
    let cancelled = false;
    const ojcId = job?.ojc_id;
    if (!isOjc || !ojcId) {
      setOjcFetchResolved(true);
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/ojc/${ojcId}`, { headers: getAuthHeader() });
        if (!cancelled) {
          setOjcDoc(r.data);
          setOjcFetchResolved(true);
        }
      } catch (e) {
        // Failing to fetch must not block print — degrade gracefully.
        if (!cancelled) {
          setOjcDoc(null);
          setOjcFetchResolved(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isOjc, job?.ojc_id, getAuthHeader]);

  useEffect(() => {
    if (autoPrint && job && process && workOrder && ojcFetchResolved && !hasTriggeredPrint.current) {
      hasTriggeredPrint.current = true;
      // Small delay to ensure component is fully rendered
      setTimeout(() => {
        handlePrint();
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPrint, job, process, workOrder, ojcFetchResolved]);

  const handlePrint = () => {
    const workers = job?.workers || [];
    // Use materials_issued from job (new structure) or fall back to material_blocking
    const materials = job?.materials_issued || workOrder?.material_blocking || [];
    const checklist = process?.checklist || [];
    const subProcesses = process?.sub_processes || [];
    
    // Parse and format the issued_at time in IST timezone
    let issuedDate;
    try {
      if (job?.issued_at) {
        issuedDate = formatInTimeZone(parseISO(job.issued_at), 'Asia/Kolkata', 'dd/MM/yyyy HH:mm');
      } else {
        issuedDate = formatInTimeZone(new Date(), 'Asia/Kolkata', 'dd/MM/yyyy HH:mm');
      }
    } catch (e) {
      // Fallback if parsing fails
      issuedDate = job?.issued_at ? format(new Date(job.issued_at), 'dd/MM/yyyy HH:mm') : format(new Date(), 'dd/MM/yyyy HH:mm');
    }
    
    // Calculate material quantities for this job
    const materialRows = materials.map((mat, idx) => {
      // For new structure, quantity is already calculated
      const issuedQty = mat.quantity_issued || mat.quantity_to_issue || 
        (mat.required_quantity ? (mat.required_quantity / workOrder.quantity) * job.quantity_issued : 0);
      return `
        <tr>
          <td style="text-align: center;">${idx + 1}</td>
          <td>${mat.item_name}</td>
          <td style="text-align: right;">${parseFloat(issuedQty).toFixed(2)}</td>
          <td style="text-align: center;">${mat.unit || ''}</td>
          <td style="text-align: center;">${parseFloat(issuedQty).toFixed(2)}</td>
        </tr>
      `;
    }).join('');

    // Add empty rows to make 25 total for materials
    const totalMaterialRows = 25;
    const emptyMaterialRows = materials.length < totalMaterialRows 
      ? [...Array(totalMaterialRows - materials.length)].map((_, idx) => `
        <tr>
          <td style="text-align: center;">${materials.length + idx + 1}</td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
      `).join('')
      : '';

    // Worker rows (fallback for renamed field: employee_name || worker_name)
    const workerRows = workers.length > 0 
      ? workers.map((w, idx) => `
        <tr>
          <td style="text-align: center;">${idx + 1}</td>
          <td>${w.employee_name || w.worker_name || ''}</td>
          <td>${w.role?.replace('_', ' ') || ''}</td>
          <td></td>
        </tr>
      `).join('')
      : '';
    
    // Empty worker rows to fill up to 5
    const emptyWorkerRows = [...Array(Math.max(5 - workers.length, 0))].map((_, idx) => `
      <tr>
        <td style="text-align: center;">${workers.length + idx + 1}</td>
        <td></td>
        <td></td>
        <td></td>
      </tr>
    `).join('');

    // BPD Received rows (5 empty rows)
    const bpdRows = [...Array(5)].map((_, idx) => `
      <tr>
        <td style="text-align: center;">${idx + 1}</td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
      </tr>
    `).join('');

    // Checklist rows from Process Master
    const checklistRows = checklist.length > 0
      ? checklist.map((item, idx) => `
        <tr>
          <td style="text-align: center;">${idx + 1}</td>
          <td>${typeof item === 'object' ? item.text : item}</td>
          <td style="text-align: center;"><span class="checkbox"></span></td>
          <td></td>
        </tr>
      `).join('')
      : '';
    
    // Empty checklist rows to make at least 5
    const minChecklistRows = 5;
    const emptyChecklistRows = checklist.length < minChecklistRows
      ? [...Array(minChecklistRows - checklist.length)].map((_, idx) => `
        <tr>
          <td style="text-align: center;">${checklist.length + idx + 1}</td>
          <td></td>
          <td style="text-align: center;"><span class="checkbox"></span></td>
          <td></td>
        </tr>
      `).join('')
      : '';

    // Sub-processes rows
    const subProcessRows = subProcesses.length > 0
      ? subProcesses.map((sp, idx) => `
        <tr>
          <td style="text-align: center;">${idx + 1}</td>
          <td>${sp.name || ''}</td>
          <td>${sp.description || ''}</td>
          <td style="text-align: center;"><span class="checkbox"></span></td>
        </tr>
      `).join('')
      : '';

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Job Card - ${job?.job_number || ''}</title>
          <style>
            @page {
              size: A4;
              margin: 8mm;
            }
            @media print {
              .page-break {
                page-break-before: always;
              }
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: Arial, sans-serif;
              font-size: 10pt;
              line-height: 1.3;
              color: #000;
            }
            .page {
              width: 100%;
              max-width: 194mm;
              min-height: 277mm;
              margin: 0 auto;
              padding: 3mm;
            }
            .header {
              text-align: center;
              border: 2px solid #000;
              padding: 8px;
              margin-bottom: 8px;
            }
            .header h1 {
              font-size: 16pt;
              font-weight: bold;
              margin: 0;
            }
            .section-title {
              font-weight: bold;
              border: 1px solid #000;
              padding: 5px 8px;
              font-size: 10pt;
              background: #f5f5f5;
              margin-bottom: 0;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 8px;
            }
            th, td {
              border: 1px solid #000;
              padding: 3px 5px;
              text-align: left;
              font-size: 8pt;
              vertical-align: top;
            }
            th {
              background: #f5f5f5;
              font-weight: bold;
            }
            .info-table td {
              height: 20px;
            }
            .info-label {
              font-weight: bold;
              background: #f5f5f5;
              width: 22%;
            }
            .info-value {
              width: 28%;
            }
            .rmt-table td {
              height: 16px;
            }
            .workers-table td {
              height: 20px;
            }
            .checkbox {
              display: inline-block;
              width: 12px;
              height: 12px;
              border: 1px solid #000;
              margin-right: 3px;
              vertical-align: middle;
            }
            .signature-row td {
              height: 40px;
              vertical-align: bottom;
              padding-bottom: 5px;
            }
            .remarks-box {
              border: 1px solid #000;
              min-height: 50px;
              padding: 5px;
              margin-bottom: 8px;
            }
            .page-title {
              text-align: center;
              font-size: 9pt;
              color: #666;
              margin-bottom: 5px;
            }
            .compact-table td {
              height: 15px;
              padding: 2px 4px;
            }
          </style>
        </head>
        <body>
          <!-- PAGE 1: FRONT -->
          <div class="page">
            <div class="header">
              <h1>JOB CARD</h1>
            </div>
            
            <!-- Job Information -->
            <table class="info-table">
              <tr>
                <td class="info-label">Job Card No.</td>
                <td class="info-value" colspan="3" style="font-weight: bold; font-size: 12pt;">${job.job_number || `${process.job_card_number}-${job.job_letter}`}</td>
              </tr>
              <tr>
                <td class="info-label">Process Name</td>
                <td class="info-value" colspan="3">${process.process_name}</td>
              </tr>
              <tr>
                <td class="info-label">Item to Produce</td>
                <td class="info-value" colspan="3">${workOrder.item_name}</td>
              </tr>
              <tr>
                <td class="info-label">Drawing No.</td>
                <td class="info-value">${workOrder.drawing_number || ''}</td>
                <td class="info-label">Machine</td>
                <td class="info-value">${job.machine || process.machine || ''}</td>
              </tr>
              <tr>
                <td class="info-label">Quantity to Produce</td>
                <td class="info-value" style="font-weight: bold; font-size: 12pt;">${job.quantity_issued} ${workOrder.unit}</td>
                <td class="info-label">Issue Date</td>
                <td class="info-value">${issuedDate}</td>
              </tr>
              <tr>
                <td class="info-label">Target Time</td>
                <td class="info-value">${process.target_time_hours || '-'} Hours</td>
                <td class="info-label">Issued By</td>
                <td class="info-value">${job.issued_by_name || '-'}</td>
              </tr>
            </table>

            <!-- Raw Materials Section - 25 rows -->
            <div class="section-title">RAW MATERIALS ISSUED (RMT)</div>
            <table class="rmt-table compact-table">
              <thead>
                <tr>
                  <th style="width: 6%; text-align: center;">S.No</th>
                  <th style="width: 50%;">Material Name</th>
                  <th style="width: 15%; text-align: right;">Req. Qty</th>
                  <th style="width: 10%; text-align: center;">Unit</th>
                  <th style="width: 19%; text-align: center;">Issued Qty</th>
                </tr>
              </thead>
              <tbody>
                ${materialRows}
                ${emptyMaterialRows}
              </tbody>
            </table>
            
            <table style="margin-top: 5px;">
              <tr>
                <td style="width: 50%; border: none; padding-left: 0;">
                  <strong>Issued By:</strong> __________________ 
                  <strong>Sign:</strong> __________________
                </td>
                <td style="width: 50%; border: none; text-align: right; padding-right: 0;">
                  <strong>Date:</strong> __________________
                </td>
              </tr>
            </table>
          </div>

          <!-- PAGE 2: BACK -->
          <div class="page page-break">
            <div class="page-title">Job Card: ${job.job_number || `${process.job_card_number}-${job.job_letter}`} (Back)</div>
            
            <!-- Workers / OJC Section -->
            ${isOjc ? `
            <div class="section-title">OUTSOURCED JOB CARD (OJC) DETAILS</div>
            <table class="compact-table" style="margin-bottom: 10px;">
              <tbody>
                <tr>
                  <td class="info-label" style="width: 25%;">OJC Number</td>
                  <td class="info-value" style="width: 25%;">${ojcDoc?.ojc_number || job?.ojc_number || '-'}</td>
                  <td class="info-label" style="width: 25%;">Status</td>
                  <td class="info-value" style="width: 25%;">${ojcDoc?.status || '-'}</td>
                </tr>
                <tr>
                  <td class="info-label">Jobber</td>
                  <td class="info-value">${ojcDoc?.jobber_name || process?.jobber_name || '-'}</td>
                  <td class="info-label">Expected Rate/Unit</td>
                  <td class="info-value">${ojcDoc?.expected_rate_per_unit != null ? Number(ojcDoc.expected_rate_per_unit).toFixed(2) : '-'}</td>
                </tr>
                <tr>
                  <td class="info-label">Quantity Issued</td>
                  <td class="info-value">${ojcDoc?.quantity_issued ?? job?.quantity_issued ?? '-'}</td>
                  <td class="info-label">Quantity Received</td>
                  <td class="info-value">${ojcDoc?.quantity_received ?? 0}</td>
                </tr>
                <tr>
                  <td class="info-label">Total Labour Charges</td>
                  <td class="info-value">${ojcDoc?.total_labour_charges != null ? Number(ojcDoc.total_labour_charges).toFixed(2) : '0.00'}</td>
                  <td class="info-label">Total OJC Cost</td>
                  <td class="info-value">${ojcDoc?.total_ojc_cost != null ? Number(ojcDoc.total_ojc_cost).toFixed(2) : '0.00'}</td>
                </tr>
              </tbody>
            </table>
            ${(ojcDoc?.receiving_batches || []).length > 0 ? `
            <div class="section-title">RECEIVING BATCHES</div>
            <table class="compact-table" style="margin-bottom: 10px;">
              <thead>
                <tr>
                  <th style="width: 10%; text-align: center;">S.No</th>
                  <th style="width: 20%; text-align: right;">Received</th>
                  <th style="width: 20%; text-align: right;">Accepted</th>
                  <th style="width: 20%; text-align: right;">Labour Charges</th>
                  <th style="width: 30%;">Received At</th>
                </tr>
              </thead>
              <tbody>
                ${(ojcDoc.receiving_batches || []).map((b, idx) => `
                  <tr>
                    <td style="text-align: center;">${idx + 1}</td>
                    <td style="text-align: right;">${b.quantity_received ?? '-'}</td>
                    <td style="text-align: right;">${b.quantity_accepted ?? '-'}</td>
                    <td style="text-align: right;">${b.labour_charges != null ? Number(b.labour_charges).toFixed(2) : '0.00'}</td>
                    <td>${b.received_at ? String(b.received_at).split('T')[0] : '-'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            ` : ''}
            ` : `
            <div class="section-title">WORKERS ASSIGNED</div>
            <table class="workers-table">
              <thead>
                <tr>
                  <th style="width: 10%; text-align: center;">S.No</th>
                  <th style="width: 45%;">Worker Name</th>
                  <th style="width: 25%;">Role</th>
                  <th style="width: 20%;">Signature</th>
                </tr>
              </thead>
              <tbody>
                ${workerRows}
                ${emptyWorkerRows}
              </tbody>
            </table>
            `}

            <!-- BPD Received Section -->
            <div class="section-title">BY-PRODUCT (BPD) RECEIVED</div>
            <table class="compact-table">
              <thead>
                <tr>
                  <th style="width: 10%; text-align: center;">S.No</th>
                  <th style="width: 40%;">BPD Item Name</th>
                  <th style="width: 15%; text-align: right;">Quantity</th>
                  <th style="width: 15%; text-align: center;">Unit</th>
                  <th style="width: 20%;">Remarks</th>
                </tr>
              </thead>
              <tbody>
                ${bpdRows}
              </tbody>
            </table>

            <!-- Checklist Section -->
            <div class="section-title">PROCESS CHECKLIST</div>
            <table class="compact-table">
              <thead>
                <tr>
                  <th style="width: 10%; text-align: center;">S.No</th>
                  <th style="width: 55%;">Check Item</th>
                  <th style="width: 15%; text-align: center;">Done</th>
                  <th style="width: 20%;">Remarks</th>
                </tr>
              </thead>
              <tbody>
                ${checklistRows}
                ${emptyChecklistRows}
              </tbody>
            </table>

            <!-- Sub-Processes Section -->
            ${subProcessRows ? `
            <div class="section-title">SUB-PROCESSES</div>
            <table class="compact-table">
              <thead>
                <tr>
                  <th style="width: 10%; text-align: center;">S.No</th>
                  <th style="width: 35%;">Sub-Process</th>
                  <th style="width: 35%;">Description</th>
                  <th style="width: 20%; text-align: center;">Completed</th>
                </tr>
              </thead>
              <tbody>
                ${subProcessRows}
              </tbody>
            </table>
            ` : ''}

            <!-- First Piece Approval -->
            <div class="section-title">FIRST PIECE APPROVAL</div>
            <table>
              <tr>
                <td class="info-label" style="width: 25%;">First Piece Approved</td>
                <td style="width: 25%;">
                  <span class="checkbox"></span> Yes &nbsp;&nbsp;&nbsp;
                  <span class="checkbox"></span> No
                </td>
                <td class="info-label" style="width: 25%;">Date & Time</td>
                <td style="width: 25%;"></td>
              </tr>
              <tr>
                <td class="info-label">Approved By (Name)</td>
                <td></td>
                <td class="info-label">Signature</td>
                <td></td>
              </tr>
            </table>

            <!-- Production Details -->
            <div class="section-title">PRODUCTION DETAILS (To be filled by Worker)</div>
            <table>
              <tr>
                <td class="info-label" style="width: 25%;">Actual Start Date/Time</td>
                <td style="width: 25%;"></td>
                <td class="info-label" style="width: 25%;">Actual End Date/Time</td>
                <td style="width: 25%;"></td>
              </tr>
              <tr>
                <td class="info-label">Completed Qty</td>
                <td></td>
                <td class="info-label">Defect Qty</td>
                <td></td>
              </tr>
              <tr>
                <td class="info-label">Total Hours Worked</td>
                <td colspan="3"></td>
              </tr>
            </table>

            <!-- Quality Check -->
            <div class="section-title">QUALITY CHECK</div>
            <table>
              <tr>
                <td class="info-label" style="width: 25%;">QC Result</td>
                <td style="width: 25%;">
                  <span class="checkbox"></span> Pass &nbsp;&nbsp;&nbsp;
                  <span class="checkbox"></span> Fail
                </td>
                <td class="info-label" style="width: 25%;">Inspected By</td>
                <td style="width: 25%;"></td>
              </tr>
              <tr>
                <td class="info-label">Date & Time</td>
                <td></td>
                <td class="info-label">Signature</td>
                <td></td>
              </tr>
              <tr>
                <td class="info-label">Rejection Reason (if any)</td>
                <td colspan="3"></td>
              </tr>
            </table>

            <!-- Remarks / Instructions -->
            <div class="section-title">REMARKS / SPECIAL INSTRUCTIONS</div>
            <div class="remarks-box">
              ${job.remarks || workOrder.remarks || ''}
            </div>

            <!-- Sign-off Section -->
            <div class="section-title">SIGN-OFF</div>
            <table>
              <tr>
                <th style="width: 50%;">Production Supervisor</th>
                <th style="width: 50%;">Store Keeper</th>
              </tr>
              <tr class="signature-row">
                <td>
                  Name: ____________________<br/>
                  Sign: ____________________ Date: ____________
                </td>
                <td>
                  Name: ____________________<br/>
                  Sign: ____________________ Date: ____________
                </td>
              </tr>
            </table>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      if (onPrintComplete) onPrintComplete();
      return;
    }
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
      window.focus();
      if (onPrintComplete) {
        onPrintComplete();
      }
    }, 300);
  };

  return null; // This component doesn't render anything visible
};

export default JobCardPrint;
